import { Manager } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import {
  connectNotificationSocket,
  everySocket,
} from './connectNotificationSocket';

type System = 'bureau' | 'umbrella';

/**
 * NX-6269: the sockets whose events come from the session's own system — job
 * progress, imports, conversations, onboarding, … Each namespace has a socket
 * on either system's host, and listeners go on both, so none has to move when
 * the session changes system; setSystem connects the session's ones.
 *
 * Each connects through connectNotificationSocket: its handshakes present the
 * session's credentials, read afresh, and it is retried once newer ones are
 * stored after the server refused or dropped it.
 */
export function createSessionSockets(hosts: Record<System, string>) {
  // A system's namespaces share one connection, opened only once a session on
  // that system needs it — also where both systems have the same host.
  const managerOf = (host: string) =>
    new Manager(host, {
      transports: ['websocket', 'polling'],
      autoConnect: false,
    });
  const managers: Record<System, Manager> = {
    bureau: managerOf(hosts.bureau),
    umbrella: managerOf(hosts.umbrella),
  };
  const pairs: Record<System, Socket>[] = [];
  let current: System | null = null;
  // An active socket is connected or on its way. Connecting it again while its
  // handshake is still out sends a second join, and the server then delivers
  // every event twice.
  const connectUnlessActive = (socket: Socket) => {
    if (!socket.active) socket.connect();
  };
  // The teardown of each socket connectNotificationSocket has connected.
  const connectedOnes = new Map<Socket, () => void>();
  const connectWithCredentials = (socket: Socket, isBureau: boolean) => {
    if (connectedOnes.has(socket)) connectUnlessActive(socket);
    else connectedOnes.set(socket, connectNotificationSocket(socket, isBureau));
  };
  const disconnect = (socket: Socket) => {
    const teardown = connectedOnes.get(socket);
    connectedOnes.delete(socket);
    if (teardown) teardown();
    else socket.disconnect();
  };

  return {
    /** The `namespace` socket of whichever system the session is on. */
    socketOf(namespace: string) {
      const pair: Record<System, Socket> = {
        bureau: managers.bureau.socket(`/${namespace}`),
        umbrella: managers.umbrella.socket(`/${namespace}`),
      };
      pairs.push(pair);
      return {
        ...everySocket(Object.values(pair)),
        get connected() {
          return current !== null && pair[current].connected;
        },
        connect() {
          if (current) connectUnlessActive(pair[current]);
        },
      };
    },

    /**
     * Connects the sockets of the session's system and disconnects the other
     * system's; `null`, no session, disconnects them all. Told the same system
     * again, as on a renewed token, it leaves connected sockets as they are.
     */
    setSystem(isBureau: boolean | null) {
      current = isBureau === null ? null : isBureau ? 'bureau' : 'umbrella';
      pairs.forEach((pair) =>
        (Object.keys(pair) as System[]).forEach((system) =>
          system === current
            ? connectWithCredentials(pair[system], system === 'bureau')
            : disconnect(pair[system]),
        ),
      );
    },

    /**
     * Connects again the session's sockets socket.io has given up on — a
     * handshake the server refused, or a socket it disconnected — with what is
     * stored now, and leaves the others as they are.
     */
    reconnect() {
      const system = current;
      if (system) pairs.forEach((pair) => connectUnlessActive(pair[system]));
    },
  };
}
